"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  DatamuseCredit,
  LookupState,
  WordChips,
} from "@/components/tools/vocabulary/word-parts";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { useDatamuse } from "@/lib/tool-kit/vocabulary/use-datamuse";
import {
  compilePattern,
  MAX_LENGTH,
  MAX_MATCHES,
  MIN_LENGTH,
  matchPattern,
  type PatternSort,
  type PatternSpec,
  patternQuery,
} from "@/lib/tool-kit/vocabulary/word-pattern";

/** English strings. A translated content file overrides these through `ui`. */
export const WORD_PATTERN_FINDER_STRINGS = {
  patternLabel: "Pattern",
  patternPlaceholder: "t??k",
  patternHint:
    "? stands for exactly one letter and * for any number of letters: c?t finds cat and cut; *ology finds words ending in -ology.",
  startsLabel: "Starts with",
  endsLabel: "Ends with",
  containsLabel: "Contains",
  lengthLabel: "Length",
  lengthAny: "Any",
  sortLabel: "Order",
  sorts: {
    common: "Most common first",
    alphabetical: "Alphabetical",
    length: "Shortest first",
  } as Record<PatternSort, string>,
  abbreviationsLabel: "Include abbreviations and names",
  clear: "Clear",
  emptyPrompt:
    "Enter a pattern such as t??k or *ology, or fill in any of the filters, to list the words that match.",
  invalid:
    "Use letters only in the filters, and letters with ? and * in the pattern.",
  impossible:
    "No word can meet all of these filters at once — check the length against the letters you have fixed.",
  heading: "Matching words",
  matchCount: "one:{count} word|other:{count} words",
  capped:
    "Datamuse returns at most {max} matches for one pattern, so some words may be missing. Add a length or more letters to see them all.",
  noResults:
    "No words match. Check the pattern for a typo, or loosen one of the filters.",
  copyAll: "Copy all",
  copied: "Copied!",
  searching: "Searching…",
  errorNetwork:
    "Could not reach the Datamuse word service. Check your connection and try again.",
  errorBusy:
    "Datamuse is receiving too many requests right now. Wait a minute and try again.",
  errorUnavailable:
    "The Datamuse word service did not answer. Try again in a moment.",
  retry: "Try again",
  credit: "Word data from the Datamuse API",
} as const;

type Strings = typeof WORD_PATTERN_FINDER_STRINGS;

const SORTS: PatternSort[] = ["common", "alphabetical", "length"];
const MAX_FIELD = 30;
const LENGTHS = Array.from(
  { length: MAX_LENGTH - MIN_LENGTH + 1 },
  (_, index) => String(MIN_LENGTH + index)
);
const NOTICES = {
  empty: "emptyPrompt",
  invalid: "invalid",
  impossible: "impossible",
} as const;
const EMPTY: PatternSpec = {
  pattern: "",
  startsWith: "",
  endsWith: "",
  contains: "",
  length: null,
};

function Results({
  words,
  capped,
  stale,
  strings,
}: {
  words: readonly string[];
  capped: boolean;
  stale: boolean;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (words.length === 0) {
    return <Notice>{strings.noResults}</Notice>;
  }
  return (
    <div className={stale ? "space-y-4 opacity-60" : "space-y-4"}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div aria-live="polite" className="space-y-1">
          <h2 className={WIDGET_H2}>{strings.heading}</h2>
          <p className={HINT}>
            {tPlural(strings.matchCount, locale, words.length, {
              count: fmt(words.length),
            })}
          </p>
        </div>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyAll}
          small
          text={words.join("\n")}
        />
      </div>
      {capped ? (
        <p className={HINT}>{t(strings.capped, { max: fmt(MAX_MATCHES) })}</p>
      ) : null}
      <WordChips words={words} />
    </div>
  );
}

export default function WordPatternFinderWidget() {
  const strings = useToolUi(WORD_PATTERN_FINDER_STRINGS);
  const fmt = useToolNumber();
  const [spec, setSpec] = useState<PatternSpec>(EMPTY);
  const [sort, setSort] = useState<PatternSort>("common");
  const [abbreviations, setAbbreviations] = useState(false);
  const compiled = compilePattern(spec);
  const queries = compiled.ok
    ? compiled.compiled.patterns.map(patternQuery)
    : [];
  const lookup = useDatamuse(queries, 400);
  const answered = compiled.ok && lookup.results.length > 0;
  const words =
    answered && compiled.ok
      ? matchPattern(lookup.results, compiled.compiled.test, {
          sort,
          abbreviations,
        }).map((match) => match.word)
      : [];
  const stale =
    lookup.status === "loading" || lookup.answered !== queries.join("\n");

  const isEmpty =
    !(spec.pattern || spec.startsWith || spec.endsWith || spec.contains) &&
    spec.length === null;

  function update(key: keyof PatternSpec, value: string) {
    setSpec((current) => ({ ...current, [key]: value.slice(0, MAX_FIELD) }));
  }

  function body() {
    if (!compiled.ok) {
      return <Notice>{strings[NOTICES[compiled.reason]]}</Notice>;
    }
    return (
      <>
        <LookupState
          failure={lookup.failure}
          onRetry={lookup.retry}
          status={lookup.status}
          strings={strings}
        />
        {answered ? (
          <Results
            capped={lookup.results.some(
              (answer) => answer.length >= MAX_MATCHES
            )}
            stale={stale}
            strings={strings}
            words={words}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <TextField
        hint={strings.patternHint}
        id="pattern-pattern"
        label={strings.patternLabel}
        mono
        onChange={(value) => update("pattern", value)}
        placeholder={strings.patternPlaceholder}
        value={spec.pattern}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="pattern-starts"
          label={strings.startsLabel}
          mono
          onChange={(value) => update("startsWith", value)}
          value={spec.startsWith}
        />
        <TextField
          id="pattern-ends"
          label={strings.endsLabel}
          mono
          onChange={(value) => update("endsWith", value)}
          value={spec.endsWith}
        />
        <TextField
          id="pattern-contains"
          label={strings.containsLabel}
          mono
          onChange={(value) => update("contains", value)}
          value={spec.contains}
        />
        <SelectField
          id="pattern-length"
          label={strings.lengthLabel}
          onChange={(value) =>
            setSpec((current) => ({
              ...current,
              length: value === "any" ? null : Number(value),
            }))
          }
          options={[
            { value: "any", label: strings.lengthAny },
            ...LENGTHS.map((value) => ({ value, label: fmt(Number(value)) })),
          ]}
          value={spec.length === null ? "any" : String(spec.length)}
        />
        <SelectField
          id="pattern-sort"
          label={strings.sortLabel}
          onChange={setSort}
          options={SORTS.map((value) => ({
            value,
            label: strings.sorts[value],
          }))}
          value={sort}
        />
        <div className="flex items-end">
          <button
            className={BUTTON_SECONDARY}
            disabled={isEmpty}
            onClick={() => setSpec(EMPTY)}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </div>
      <Checkbox
        checked={abbreviations}
        label={strings.abbreviationsLabel}
        onChange={setAbbreviations}
      />
      {body()}
      <DatamuseCredit label={strings.credit} />
    </>
  );
}
